import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../content/performanceIncomeDashboard";
import type { IncomeTileConfig } from "./IncomeBreakdown.types";
import { SectionDivider } from "./Components/SectionDivider/SectionDivider";
import { IncomeTileContent } from "./Components/IncomeTileContent/IncomeTileContent";
import { StackedIncomeTile } from "./Components/IncomeTileContent/StackedIncomeTile/StackedIncomeTile";
import { SubIncomeTile } from "./Components/IncomeTileContent/SubIncomeTile/SubIncomeTile";
import "./IncomeBreakdown.scss";

export type { TileHeader, IncomeTileConfig } from "./IncomeBreakdown.types";

export interface IncomeBreakdownProps {
  estimatedIncomeCard?: ReactNode;
  tiles?: IncomeTileConfig[];
  leaderTiles?: IncomeTileConfig[];
  corePlanTiles?: IncomeTileConfig[];
  corePlanLeftTiles?: IncomeTileConfig[];
  corePlanRightTiles?: IncomeTileConfig[];
  corePlusTiles?: IncomeTileConfig[];
  bottomLinkCards?: ReactNode;
}

const renderTile = (tile: IncomeTileConfig, index: number) => {
  if (tile.stackedTiles) {
    return <StackedIncomeTile key={index} {...tile} stackedTiles={tile.stackedTiles} />;
  }

  if (tile.subTiles) {
    return <SubIncomeTile key={index} subTiles={tile.subTiles} />;
  }

  const hasRequirements = !!(
    tile.legsProgress ||
    tile.kpiTrackers ||
    tile.pvRequirement ||
    tile.pvRequirements ||
    tile.metricCard ||
    tile.pinRequirement ||
    tile.pinRequirements ||
    tile.linkRows ||
    tile.statusBadge
  );

  return (
    <div
      key={index}
      className={`income-breakdown__tile${hasRequirements ? "" : " income-breakdown__tile--minimal"}`}
      data-testid={`income-breakdown__tile${tile.header?.labelTestId}`}
    >
      <IncomeTileContent {...tile} />
    </div>
  );
};

export const IncomeBreakdown = ({
  estimatedIncomeCard,
  tiles,
  leaderTiles,
  corePlanTiles,
  corePlanLeftTiles,
  corePlanRightTiles,
  corePlusTiles,
  bottomLinkCards,
}: IncomeBreakdownProps) => {
  const { t } = useTranslation(["performanceIncomeDashboard"]);

  const hasColumnarCorePlan =
    corePlanLeftTiles && corePlanLeftTiles.length > 0 && corePlanRightTiles && corePlanRightTiles.length > 0;

  const allTileGroups: {
    divider?: { title: string; variant?: "default" | "blue"; testId?: string };
    tiles: IncomeTileConfig[];
    columnar?: { left: IncomeTileConfig[]; right: IncomeTileConfig[] };
  }[] = [];

  if (tiles && tiles.length > 0) allTileGroups.push({ tiles });
  if (leaderTiles && leaderTiles.length > 0) allTileGroups.push({ tiles: leaderTiles });

  if (hasColumnarCorePlan) {
    allTileGroups.push({
      divider: {
        title: String(t("incentives.corePlanBonuses", defaultContent.incentives.corePlanBonuses)),
        variant: "blue",
        testId: "core-plan-bonuses-title",
      },
      tiles: [],
      columnar: { left: corePlanLeftTiles, right: corePlanRightTiles },
    });
  } else if (corePlanTiles && corePlanTiles.length > 0) {
    allTileGroups.push({
      divider: {
        title: String(t("incentives.corePlanBonuses", defaultContent.incentives.corePlanBonuses)),
        variant: "blue",
        testId: "core-plan-bonuses-title",
      },
      tiles: corePlanTiles,
    });
  }

  if (corePlusTiles && corePlusTiles.length > 0) {
    allTileGroups.push({
      divider: {
        title: String(t("incentives.corePlusIncentives", defaultContent.incentives.corePlusIncentives)),
        variant: "blue",
        testId: "core-plus-incentives-title",
      },
      tiles: corePlusTiles,
    });
  }

  const allTiles = allTileGroups.flatMap((g) => [
    ...g.tiles,
    ...(g.columnar ? [...g.columnar.left, ...g.columnar.right] : []),
  ]);
  const gridItemCount = allTiles.reduce((acc, t) => acc + (t.stackedTiles ? 2 : 1), 0);
  const gridClass = `income-breakdown__grid${
    gridItemCount > 1 && gridItemCount % 2 !== 0 ? " income-breakdown__grid--odd-count" : ""
  }`;

  let tileIndex = 0;

  return (
    <div className="income-breakdown" data-testid="income-breakdown">
      <SectionDivider
        title={String(t("incentives.incomeBreakdown", defaultContent.incentives.incomeBreakdown))}
        data-testid="income-breakdown-title"
      />
      {estimatedIncomeCard}
      {allTileGroups.length > 0 && (
        <div className={gridClass}>
          {allTileGroups.map((group, gi) => (
            <Fragment key={gi}>
              {group.divider && (
                <div className="income-breakdown__grid-divider">
                  <SectionDivider
                    title={group.divider.title}
                    variant={group.divider.variant}
                    data-testid={group.divider.testId}
                  />
                </div>
              )}
              {group.columnar ? (
                <div className="income-breakdown__columnar">
                  <div className="income-breakdown__columnar-left">
                    {group.columnar.left.map((tile) => renderTile(tile, tileIndex++))}
                  </div>
                  <div className="income-breakdown__columnar-right">
                    {group.columnar.right.map((tile) => renderTile(tile, tileIndex++))}
                  </div>
                </div>
              ) : (
                group.tiles.map((tile) => renderTile(tile, tileIndex++))
              )}
            </Fragment>
          ))}
        </div>
      )}
      {bottomLinkCards && <div className="income-breakdown__bottom-links">{bottomLinkCards}</div>}
    </div>
  );
};
